import React, { useRef, useState } from "react";
import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react";
import { CaretUp, FileText, Plus, X } from "@phosphor-icons/react";

const ACCEPT = ".pdf,.docx,.xlsx,.csv";
const extOf = (name) => (name.split(".").pop() || "").toUpperCase().slice(0, 4);

// "Sources" attachment card shown at the foot of a task. Lime dashed dropzone
// + a "Use from library" link, matching the platform's sources panel. Files
// are local mock state.
function SourcesCard({ files, onChange }) {
  const inputRef = useRef();
  const [collapsed, setCollapsed] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const addFiles = (fileList) => {
    const next = Array.from(fileList).map((f, i) => ({
      id: `${f.name}-${f.size}-${i}-${f.lastModified || ""}`,
      name: f.name,
      ext: extOf(f.name),
    }));
    onChange([...(files || []), ...next]);
  };
  const remove = (id) => onChange((files || []).filter((f) => f.id !== id));

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={6} bg="white">
      <Flex justify="space-between" align="flex-start" gap={3} mb={1}>
        <HStack spacing={2} color="limeShade.600">
          <FileText size={18} weight="fill" />
          <Text fontSize="13px" fontWeight="700" letterSpacing="0.06em" textTransform="uppercase">Sources</Text>
        </HStack>
        <Box as="button" color="gray.400" _hover={{ color: "gray.600" }} onClick={() => setCollapsed((c) => !c)}>
          <CaretUp size={18} style={{ transform: collapsed ? "rotate(180deg)" : "none" }} />
        </Box>
      </Flex>
      <Text fontSize="20px" fontWeight="700" color="darkTeal" mb={1}>Is additional information required?</Text>
      <Text fontSize="14px" color="gray.500" mb={collapsed ? 0 : 4}>
        Attach any documentary evidence needed to support this data or methodology.
      </Text>

      {!collapsed && (
        <>
          {files?.length > 0 && (
            <Flex direction="column" mb={3}>
              {files.map((f) => (
                <Flex key={f.id} align="center" gap={3} py={2} borderBottom="1px solid" borderColor="gray.100">
                  <Flex w="24px" h="28px" align="center" justify="center" borderRadius="sm" border="1px solid" borderColor="gray.300" flexShrink={0}>
                    <Text fontSize="8px" fontWeight="700" color="gray.500">{f.ext}</Text>
                  </Flex>
                  <Text flex={1} fontSize="14px" color="gray.700" noOfLines={1}>{f.name}</Text>
                  <Box as="button" color="gray.400" _hover={{ color: "gray.600" }} onClick={() => remove(f.id)}><X size={16} /></Box>
                </Flex>
              ))}
            </Flex>
          )}

          <Flex
            direction="column"
            align="center"
            justify="center"
            textAlign="center"
            border="1.5px dashed"
            borderColor={dragOver ? "limeShade.500" : "limeShade.300"}
            bg="lightLime"
            borderRadius="lg"
            py={8}
            px={6}
            cursor="pointer"
            transition="all 0.15s"
            _hover={{ borderColor: "limeShade.500" }}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <Box color="limeShade.600" mb={2}><Plus size={28} /></Box>
            <Text fontSize="16px" fontWeight="700" color="darkTeal">Drop or click to upload</Text>
            <Text fontSize="13px" color="gray.500" mt={1}>PDF, DOCX, XLSX, CSV · 50 MB</Text>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              multiple
              hidden
              onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ""; }}
            />
          </Flex>

          <Button variant="link" color="limeShade.600" fontWeight="600" fontSize="14px" mt={3} leftIcon={<Plus size={14} weight="bold" />} _hover={{ textDecoration: "none" }}>
            Use from library
          </Button>
        </>
      )}
    </Box>
  );
}

export default SourcesCard;
